<script lang="ts">
  import type { PortableTextBlock } from "@portabletext/types";
  import type { ImageWithMeta, SanityHero, Teaser } from "$lib/types/shared";
  import type { Nettbutikk, ProductCategoryPage } from "$lib/types/products";
  import ListArticles from "$lib/ListArticles.svelte";
  import TeaserSection from "$lib/TeaserSection.svelte";
  import Button from "$lib/Button.svelte";
  import ProductsTeaser from "./ProductsTeaser.svelte";
  import FrontPageHero from "./FrontPageHero.svelte";
  import MetaData from "$lib/MetaData.svelte";
  import AboutUs from "./AboutUs.svelte";
  import { fetchArticles } from "$lib/utils/fetchArticles";

  const description =
    "For Ishavskraft er det viktig at du forstår hva du kjøper. Hos oss kan du enkelt sammenligne våre strømavtaler, og velge den avtalen som passer best deg og ditt forbruk.";

  // Fetch articles when in viewport
  const intersectionObserverAction = (element: HTMLElement) => {
    const observer = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting) {
        observer.disconnect();
        articles = await fetchArticles({ last: 3, category: "Nyheter" });
      }
    });

    observer.observe(element);
  };

  interface Props {
    data: {
      image?: string;
      imageAlt?: string;
      imageWidth?: number;
      imageHeight?: number;
      frontPage?: {
        title: string;
        hero: SanityHero;
        image: ImageWithMeta;
        interactiveModuleTitle: string;
        interactiveModuleContent: PortableTextBlock[];
        interactiveModuleCallMeButton?: boolean;
        interactiveModuleCallMeButtonText?: string;
        subImage: ImageWithMeta;
      };
      app_teaser?: Teaser;
      category_pages: Readonly<ProductCategoryPage[]>;
      nettbutikk: Readonly<Nettbutikk>;
    };
  }

  let { data }: Props = $props();

  let articles = $state();
  let app_teaser = $derived(data?.app_teaser);
  let product_category_pages = $derived(data?.category_pages);
  let nettbutikkGenericContent = $derived(data?.nettbutikk);
</script>

<MetaData
  data={{
    title: `${data?.frontPage?.title ?? "Ishavskraft"}`,
    description,
    image: data?.image,
    imageAlt: data?.imageAlt,
    imageWidth: data?.imageWidth,
    imageHeight: data?.imageHeight,
    type: "website",
  }}
/>

<div class="front-page-content">
  <div class="mb-16 relative z-1">
    <FrontPageHero
      headingElement="h1"
      pictureObject={data?.frontPage?.image}
      hero={data?.frontPage?.hero}
    />
  </div>

  <AboutUs
    pictureObject={data?.frontPage?.subImage}
    title={data?.frontPage?.interactiveModuleTitle}
    content={data?.frontPage?.interactiveModuleContent}
    showCallMeButton={data?.frontPage?.interactiveModuleCallMeButton}
    callMeButtonText={data?.frontPage?.interactiveModuleCallMeButtonText}
    class="my-16 mx-.25edge-x px-.75edge-x laptop:mx-edge-x rounded-2xl bg-$skodde py-8 laptop:px-6"
  />

  {#if product_category_pages}
    <ProductsTeaser {nettbutikkGenericContent} class="relative z-1" />
  {/if}

  <section
    class="my-16 mx-.25edge-x px-.75edge-x laptop:mx-edge-x rounded-2xl bg-$isbre py-8 laptop:px-6"
  >
    <div class="my-16">
      <h2>Hva koster vaskemaskinen i dag?</h2>
      <p class="text-body">
        Svaret på det finner du i vår strømkalkulkator. Trykk på knappen nedenfor og test ut!
      </p>
      <Button class="mb-4 z-1" href="/privat/hva-koster" arrow>Test vår strømkalkulator</Button>
    </div>
  </section>

  <section class="my-16 pt-16 isolate relative" use:intersectionObserverAction>
    <div class="mx-edge-x flex flex-wrap items-center gap-4 [&>*]:my-0">
      <h2 class="p-name">Artikler og sparetips</h2>
      <p>
        <Button href="/om/aktuelt" class="button" arrow>Alle artikler</Button>
      </p>
    </div>
    <ListArticles
      {articles}
      displayFields={{ date: true }}
      horizontalScroll={true}
      fixedHeight={true}
      class="relative"
      --bg-card="transparent"
    />
    <div class="triangle-graphic absolute z--1"></div>
  </section>

  {#if app_teaser}
    <TeaserSection class="my-16" circleImage={true} teaser={app_teaser} href="/app" />
  {/if}
</div>

<style lang="postcss">
  .front-page-content {
    --text-h2-lg: var(--text-h2);
  }

  @media (--laptop) {
    .front-page-content {
      --w-cover-x: 2.25vw;
    }
  }

  @media (--wide) {
    .front-page-content {
      --w-cover-x: 1.75vw;
    }
  }

  .triangle-graphic {
    background: var(--triangle) no-repeat center / contain;
    rotate: 65deg;
    top: calc(-1 * (12.5% + 5vh));
    left: calc(var(--space-edge-x) - 2em);
    width: min(75vw, 400px);
    height: min(75vw, 400px);
  }
</style>
